import React, { useState, useEffect } from 'react';
import { RefreshCw, X, AlertCircle, List, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getTranscriptionBackupsByModule, deleteTranscriptionBackup, clearOldBackups, FailedTranscription } from '../services/transcriptionBackupService';

interface RetryTranscriptionButtonProps {
  moduleName: string;
  onRetry: (backup: FailedTranscription) => Promise<void>;
  isDark: boolean;
}

const formatBackupDuration = (seconds?: number): string => {
  if (seconds === undefined || seconds === null) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatBackupDate = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const RetryTranscriptionButton: React.FC<RetryTranscriptionButtonProps> = ({ moduleName, onRetry, isDark }) => {
  const [backups, setBackups] = useState<FailedTranscription[]>([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [isListOpen, setIsListOpen] = useState(false);

  const loadBackups = async () => {
    const items = await getTranscriptionBackupsByModule(moduleName);
    const now = Date.now();
    const filtered = items.filter(item => item.isError || (now - item.timestamp) > 40000);
    setBackups(filtered);
  };

  useEffect(() => {
    // Limpieza automática de pendientes con más de 7 días sin resolver, una vez al montar
    clearOldBackups(7 * 24 * 60 * 60 * 1000).finally(loadBackups);
    const interval = setInterval(loadBackups, 5000);
    return () => clearInterval(interval);
  }, [moduleName]);

  const handleRetry = async (backup: FailedTranscription) => {
    setIsRetrying(true);
    setRetryingId(backup.id);
    try {
      await onRetry(backup);
      await deleteTranscriptionBackup(backup.id);
      await loadBackups();
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
      setRetryingId(null);
    }
  };

  const handleDismiss = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await deleteTranscriptionBackup(id);
    await loadBackups();
  };

  const handleClearAll = async () => {
    for (const b of backups) {
      await deleteTranscriptionBackup(b.id);
    }
    await loadBackups();
    setIsListOpen(false);
  };

  if (backups.length === 0) return null;

  const sortedDesc = [...backups].sort((a, b) => b.timestamp - a.timestamp);
  const latestBackup = sortedDesc[0];
  const visibleList = sortedDesc.slice(0, 10);

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="fixed bottom-24 md:bottom-6 right-6 z-50"
        >
          <div 
            className={`flex items-center gap-3 p-3 rounded-2xl border shadow-2xl transition-all
              ${isDark ? 'bg-orange-950/40 border-orange-500/50 text-orange-200' : 'bg-orange-50 border-orange-200 text-orange-800'}`}
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={14} className="text-orange-500" />
                <span className="text-[10px] font-black uppercase tracking-wider">Audio Pendiente</span>
              </div>
              <p className="text-[11px] font-medium opacity-80 leading-tight">
                Se detectó un dictado no enviado del módulo {moduleName === 'modular1' ? 'General' : moduleName === 'phd_drafting' ? 'Redacción' : 'IA'}.
                {backups.length > 1 && ` (${backups.length} pendientes)`}
              </p>
            </div>

            <div className="flex items-center gap-2 ml-2">
              {backups.length > 1 && (
                <button
                  onClick={() => setIsListOpen(true)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-xl text-[11px] font-black uppercase transition-all active:scale-95
                    ${isDark ? 'bg-orange-900/50 text-orange-200 hover:bg-orange-900/70' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
                >
                  <List size={14} />
                  Lista
                </button>
              )}
              <button
                onClick={() => handleRetry(latestBackup)}
                disabled={isRetrying}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase transition-all active:scale-95
                  ${isDark ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
              >
                {isRetrying ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Reenviar
              </button>
              <button
                onClick={(e) => handleDismiss(e, latestBackup.id)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {isListOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsListOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md max-h-[70vh] flex flex-col rounded-[32px] border shadow-2xl overflow-hidden ${isDark ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-gray-100'}`}
            >
              <div className={`p-5 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                <div>
                  <h3 className={`text-sm font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>Audios Pendientes</h3>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">Últimos {visibleList.length} de {backups.length}</p>
                </div>
                <button onClick={() => setIsListOpen(false)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                {visibleList.map(b => (
                  <div key={b.id} className={`flex items-center justify-between gap-2 p-3 rounded-2xl border ${isDark ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                    <div className="min-w-0">
                      <p className={`text-[11px] font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{formatBackupDate(b.timestamp)}</p>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Duración: {formatBackupDuration(b.durationSeconds)}{b.isError ? ' · Falló al enviar' : ''}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleRetry(b)}
                        disabled={isRetrying}
                        className={`p-2 rounded-lg transition-all ${isDark ? 'bg-orange-900/50 text-orange-300 hover:bg-orange-900/70' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                        title="Reenviar"
                      >
                        {retryingId === b.id ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                      </button>
                      <button
                        onClick={(e) => handleDismiss(e, b.id)}
                        className={`p-2 rounded-lg transition-all ${isDark ? 'hover:bg-red-900/30 text-gray-400 hover:text-red-400' : 'hover:bg-red-50 text-gray-400 hover:text-red-500'}`}
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className={`p-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                <button
                  onClick={handleClearAll}
                  className={`w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isDark ? 'bg-red-900/20 text-red-400 hover:bg-red-900/40' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                >
                  Vaciar Todo
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RetryTranscriptionButton;
